"use client"

import Link from "next/link"
import { useRef } from "react"
import { FiHelpCircle, FiSearch, FiX } from "react-icons/fi"
import { cn } from "@/lib/utils"

interface ResourcesHeaderProps {
    searchQuery: string
    onSearchChange: (val: string) => void
    activeCategory: string
    onCategoryChange: (id: string) => void
    categories: { id: string; title: string; count: number }[]
    totalCount: number
    dailyOnly: boolean
    onToggleDailyOnly: () => void
    onOpenRequestModal?: () => void
}

export function ResourcesHeader({
    searchQuery,
    onSearchChange,
    activeCategory,
    onCategoryChange,
    categories,
    totalCount,
    dailyOnly,
    onToggleDailyOnly,
    onOpenRequestModal,
}: ResourcesHeaderProps) {
    const searchInputRef = useRef<HTMLInputElement>(null)

    return (
        <header className="flex flex-col gap-8 border-border/70 border-b pb-8 dark:border-border/40">
            {/* Top Navigation / Breadcrumbs */}
            <div className="flex items-center justify-between">
                <nav
                    aria-label="Breadcrumb navigation"
                    className="flex items-center gap-3 font-mono text-xs uppercase tracking-widest sm:gap-4"
                >
                    <Link
                        href="/"
                        className="text-muted-foreground transition-colors hover:text-foreground"
                    >
                        Home
                    </Link>
                    <span
                        className="text-muted-foreground/60 dark:text-muted-foreground/50"
                        aria-hidden="true"
                    >
                        /
                    </span>
                    <Link
                        href="/resources"
                        className="text-muted-foreground transition-colors hover:text-foreground"
                    >
                        Resources
                    </Link>
                </nav>
            </div>

            {/* Title & Editorial Description */}
            <div className="flex flex-col gap-3">
                <h1 className="font-medium text-4xl tracking-tighter sm:text-5xl md:text-6xl">
                    RESOURCES
                    <span className="text-green-500 dark:text-green-400">
                        .
                    </span>
                </h1>
                <p className="max-w-2xl font-light text-base text-muted-foreground leading-relaxed sm:text-lg">
                    Personal apps, autonomous coding agents, agent skills,
                    frontier LLMs, terminal workflows, and design systems I open
                    and rely on every day.
                </p>
            </div>

            {/* Search & Filter Bar (Inspired by Reference Banner) */}
            <div className="flex flex-col gap-4 border border-border/70 bg-card/30 p-4 sm:p-5 dark:border-border/40 dark:bg-card/15">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    {/* Search Input */}
                    <div className="relative flex-1">
                        <FiSearch
                            className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground/60"
                            aria-hidden="true"
                        />
                        <input
                            ref={searchInputRef}
                            type="text"
                            aria-label="Search resources"
                            value={searchQuery}
                            onChange={(e) => onSearchChange(e.target.value)}
                            placeholder="Search tools, agents, skills, LLMs, shortcuts..."
                            className="w-full border border-border/70 bg-background py-2 pr-9 pl-9 text-base text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-foreground/40 sm:text-sm dark:border-border/40"
                        />
                        {searchQuery && (
                            <button
                                type="button"
                                onClick={() => {
                                    onSearchChange("")
                                    searchInputRef.current?.focus()
                                }}
                                className="absolute top-1/2 right-3 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                aria-label="Clear search"
                            >
                                <FiX className="size-4" />
                            </button>
                        )}
                    </div>

                    {/* Filter & Action Buttons */}
                    <div className="flex flex-wrap items-center gap-2">
                        {/* Daily Driver Filter Switch */}
                        <button
                            type="button"
                            onClick={onToggleDailyOnly}
                            aria-pressed={dailyOnly}
                            className={cn(
                                "inline-flex items-center justify-center gap-1.5 border px-3 py-2 font-mono text-xs transition-all",
                                dailyOnly
                                    ? "border-green-500/50 bg-green-500/10 font-medium text-green-600 dark:border-green-500/30 dark:text-green-400"
                                    : "border-border/70 bg-background text-muted-foreground hover:bg-muted hover:text-foreground dark:border-border/40"
                            )}
                        >
                            <span
                                className={cn(
                                    "size-1.5 transition-colors",
                                    dailyOnly
                                        ? "bg-green-500"
                                        : "bg-muted-foreground/40"
                                )}
                            />
                            Everyday Stack
                        </button>

                        {/* Request Tool Recommendation */}
                        {onOpenRequestModal && (
                            <button
                                type="button"
                                onClick={onOpenRequestModal}
                                className="inline-flex items-center justify-center gap-1.5 border border-border/70 bg-background px-3 py-2 font-mono text-muted-foreground text-xs transition-all hover:border-foreground/40 hover:bg-muted hover:text-foreground dark:border-border/40"
                            >
                                <FiHelpCircle className="size-3.5 text-muted-foreground/70" />
                                <span>Ask for a Tool</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* Category Tabs */}
                <fieldset className="scrollbar-hide -mx-4 flex min-w-0 items-center gap-1.5 overflow-x-auto border-0 px-4 pt-1 pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0">
                    <legend className="sr-only">Resource categories</legend>
                    <button
                        type="button"
                        onClick={() => onCategoryChange("all")}
                        aria-pressed={activeCategory === "all"}
                        className={cn(
                            "shrink-0 border px-2.5 py-1 font-mono text-xs uppercase tracking-wider transition-all",
                            activeCategory === "all"
                                ? "border-foreground bg-foreground text-background"
                                : "border-border/70 bg-muted/20 text-muted-foreground hover:border-foreground/40 hover:bg-muted/60 hover:text-foreground dark:border-border/40 dark:bg-muted/10"
                        )}
                    >
                        All ({totalCount})
                    </button>

                    {categories.map((cat) => (
                        <button
                            key={cat.id}
                            type="button"
                            onClick={() => onCategoryChange(cat.id)}
                            aria-pressed={activeCategory === cat.id}
                            className={cn(
                                "shrink-0 border px-2.5 py-1 font-mono text-xs uppercase tracking-wider transition-all",
                                activeCategory === cat.id
                                    ? "border-foreground bg-foreground text-background"
                                    : "border-border/70 bg-muted/20 text-muted-foreground hover:border-foreground/40 hover:bg-muted/60 hover:text-foreground dark:border-border/40 dark:bg-muted/10"
                            )}
                        >
                            {cat.title} ({cat.count})
                        </button>
                    ))}
                </fieldset>
            </div>
        </header>
    )
}
